const API_URL = import.meta.env.VITE_API_URL || 'http://localhost/track.php'; // In prod, this should rely on relative path or env var

export interface TrackingData {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
}

declare global {
    interface Window {
        fbq: any;
        _fbq: any;
    }
}

class TrackingService {
    private visitorId: string;
    private utms: TrackingData = {};

    constructor() {
        // Load or Generate Visitor ID
        let storedId = localStorage.getItem('visitor_id');
        if (!storedId) {
            // Simple random ID generation (v4-like)
            storedId = crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
            localStorage.setItem('visitor_id', storedId);
        }
        this.visitorId = storedId;
        this.parseUTMs();
    }

    private parseUTMs() {
        const params = new URLSearchParams(window.location.search);
        this.utms = {
            utm_source: params.get('utm_source') || undefined,
            utm_medium: params.get('utm_medium') || undefined,
            utm_campaign: params.get('utm_campaign') || undefined,
            utm_content: params.get('utm_content') || undefined,
            utm_term: params.get('utm_term') || undefined,
        };
    }

    public async initSession() {
        // Check if session already initialized this "visit" to avoid dupe starts? 
        // For simplicity, we track session start on every app load (refresh).
        // The backend uses INSERT IGNORE for visitors table, so it's safe.

        await this.trackEvent('session_start', {
            ...this.utms,
            device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
        });
    }

    public async trackEvent(eventName: string, data: any = {}) {
        try {
            // In production, point to the actual PHP file.
            // Since this is static host, we might need absolute URL if PHP is effectively on a different sub-path or separate backend.
            // Assuming for Hostinger, this React app is index.html and track.php is side-by-side.

            const endpoint = '/track.php';

            // If running on localhost (vite), we might fail to hit ./track.php unless we proxy or mock.
            // We will safeguard simply:

            const payload = {
                visitor_id: this.visitorId,
                event_name: eventName,
                event_data: data,
                ...this.utms // Send UTMs with every event or just session? 
                // Our PHP logic only uses UTMs for 'session_start' to populate visitors table.
                // But let's pass them in the payload root for session_start, 
                // and for others it just goes into event_data if we wanted.
                // Actually, for session_start, we merge them into the root payload in the call above.
            };

            // Specific handling for session_start payload structure expected by PHP
            if (eventName === 'session_start') {
                Object.assign(payload, this.utms);
                if (data.device_type) {
                    (payload as any).device_type = data.device_type;
                }
            }

            await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            // --- FACEBOOK PIXEL INTEGRATION ---
            // Mapping Internal Events -> Standard FB Events
            if (typeof window.fbq === 'function') {
                switch (eventName) {
                    case 'landing_view':
                        window.fbq('track', 'ViewContent', { content_name: 'Landing Page' });
                        break;
                    case 'analysis_start': // Quiz Completed / Analysis Started
                        window.fbq('track', 'Lead'); // Or 'Schedule' / 'SubmitApplication'
                        break;
                    case 'sales_view':
                        window.fbq('track', 'ViewContent', { content_name: 'Sales Page', content_category: data.profile });
                        break;
                    case 'offer_click':
                        window.fbq('track', 'InitiateCheckout', {
                            value: data.price || 27.00,
                            currency: 'BRL',
                            content_name: 'TrendFunnel VIP Access'
                        });
                        break;
                    // Optional: Track individual quiz steps as custom events?
                    // case 'quiz_step_complete': window.fbq('trackCustom', 'QuizStep', { step: data.step }); break;
                }
            }

        } catch (e) {
            console.error('Tracking Error:', e);
        }
    }
}

export const tracking = new TrackingService();
